import { css } from "@linaria/core";
import React from "react";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import { colors, fonts, spacings } from "../Theme";
import type { Props as FieldProps } from "../Form/Field";
import Field from "../Form/Field";

export interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  defaultValue?: string;
  batch?: boolean;
  dirty?: boolean;
  pristine?: boolean;
  setValue?: React.Dispatch<React.SetStateAction<string>>;
}

export const input = css`
  &:is(input) {
    all: unset;
    appearance: none;
    padding: 9px 0;
    font: ${fonts.body1};
    padding-right: ${spacings.s};
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ::placeholder {
      color: ${colors.carbon900};
    }

    ::placeholder,
    &:disabled {
      opacity: 0.5;
    }

    &:disabled {
      cursor: not-allowed;
    }

    &::-webkit-calendar-picker-indicator {
      cursor: pointer;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }

    &[type="search"] {
      &::-webkit-search-cancel-button {
        -webkit-appearance: none;

        @supports not (-moz-appearance: none) {
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" d="M0 0h24v24H0z" /><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z" /></svg>');
          filter: invert(69%) sepia(9%) saturate(317%) hue-rotate(186deg) brightness(84%)
            contrast(87%);
          height: 20px;
          width: 20px;
          margin-right: -2px;
          transition: filter 144ms linear;

          &:hover {
            cursor: pointer;
            filter: invert(45%) sepia(8%) saturate(546%) hue-rotate(188deg) brightness(89%)
              contrast(89%);
          }
        }
      }
    }

    &[type="number"] {
      -moz-appearance: textfield; /* Firefox */
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      box-shadow: 0 0 0 30px ${colors.hydrogen} inset;
      -webkit-text-fill-color: ${colors.carbon900};
    }
  }
`;

const HTMLInputSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;

/**
 *
 * @param P Component properties
 * @param P.afterInputSlot Inside input container, allow passing an element. Currently used in SearchInputV2 to handle clear button fallback for Firefox.
 * @param P.error If any, display an error message.
 * @param P.label If any, display an input label.
 * @param P.defaultValue If any, fill input with a default value.
 * [**NOTE**]: Currently, revert behavior is handeld in an uncontrolled way,
 * depending whether defaultValue is provided or not.
 * The main issue is undefined defaultValue with controlled input
 * pass undefined string value.
 * @param P.beforeInputSlot Inside input container, allow passing an element. Currently used in SearchInputV2 to handle search Icon.
 * @param P.batch Distinguish whether we use InputV2 in multi-edit mode. Prior to handle revert properly.
 * @param P.dirty **UseInput hook specific:** Has value changed from default value
 * @param P.pristine **UseInput hook specific:** Has value change at least once
 * @param P.setValue **UseInput hook specific:** Setter for controlled input
 * @param forwardRef HTMLInputElement
 * @returns JSX.Element
 */
function Input(
  {
    error,
    displayErrorMessage,
    afterInputSlot,
    label,
    hint,
    required,
    onChange,
    value,
    defaultValue,
    beforeInputSlot,
    dirty,
    pristine: _pristing,
    setValue: _setValue,
    className,
    placeholder = "",
    batch = false,
    revertable = true,
    style,
    tooltipOverlay,
    ...props
  }: Props & Omit<FieldProps, "id">,
  forwardRef: React.Ref<HTMLInputElement>,
) {
  const id = `input-${Date.now()}${Math.floor(Math.random() * 1000)}`;
  const ref = useEnsuredForwardedRef(forwardRef);
  const canBeReverted = React.useMemo(() => {
    if (batch) return dirty;
    return (
      (typeof dirty === "undefined" ? true : dirty) &&
      ((typeof defaultValue === "string" && defaultValue.length > 0) ||
        typeof defaultValue !== "undefined")
    );
  }, [batch, dirty, defaultValue]);

  const handleRevert = React.useCallback(() => {
    const reset = batch ? defaultValue || "" : defaultValue;
    if (!ref.current || typeof reset !== "string") return;
    HTMLInputSetter?.call(ref.current, reset);
    ref.current.focus();
    ref.current.dispatchEvent(new Event("input", { bubbles: true }));
  }, [batch, ref, defaultValue]);

  // Uncontrolled initial default value
  React.useEffect(() => {
    if (!value && ref.current && defaultValue) ref.current.value = defaultValue;
  }, [value, ref, defaultValue]);

  return (
    <Field
      id={id}
      label={label}
      required={required}
      error={error}
      displayErrorMessage={displayErrorMessage}
      disabled={props.disabled}
      revertable={canBeReverted && revertable}
      revert={handleRevert}
      beforeInputSlot={beforeInputSlot}
      afterInputSlot={afterInputSlot}
      style={style}
      className={className}
      hint={hint}
      tooltipOverlay={tooltipOverlay}
    >
      <input
        id={id}
        ref={ref}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className={input}
        title={value?.toString()}
        {...props}
      />
    </Field>
  );
}

export default React.memo(React.forwardRef(Input));
